/* eslint-disable no-constant-condition */
import { Box, CircularProgress, Paper, Typography } from '@mui/material'
import { VirtualizedList } from 'features/activity-feed/components/VirtualizedList'
import { setActivityFeedDataAction } from 'features/activity-feed/reducers/activity-feed'
import { getActivityFeed } from 'features/activity-feed/thunks/activity-feed'
import moment from 'moment'
import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'

type UserActivityFeedViewProps = {
  userId: string
  dateStr: string
}

const UserActivityFeedView: React.FC<UserActivityFeedViewProps> = ({
  userId,
  dateStr,
}) => {
  const dispatch = useAppDispatch()
  const { isLoading } = useAppSelector(state => state.activityFeed)

  const { users } = useAppSelector(state => state.usersManagement)

  useEffect(() => {
    // Convert date string to timestamp for start/end of day
    const fromDate = moment(dateStr).startOf('day').valueOf()
    const toDate = moment(dateStr).endOf('day').valueOf()

    // Fetch activity feed for the specified user and date
    dispatch(
      getActivityFeed({
        users: [userId],
        fromDate,
        toDate,
      }),
    )

    // Clean up when the component unmounts
    return () => {
      dispatch(setActivityFeedDataAction([]))
    }
  }, [dispatch, userId, dateStr])

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        sx={{
          p: 2,
          boxShadow: 'rgba(0, 0, 0, 0.08) 0px 1px 4px',
          overflowY: 'auto',
          minHeight: '400px',
          '.empty-activity-feed-container': {
            height: '100%',
            paddingBottom: '0px',
          },
          '&:has(.empty-activity-feed-container)': {
            ...styles.center,
          },
          ...(isLoading ? styles.center : {}),
        }}
        variant="outlined"
      >
        {isLoading ? (
          <Box sx={styles.loader}>
            <CircularProgress size={25} />
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <VirtualizedList
            showDate={false}
            users={users.filter(user => user.uid === userId)}
          />
        )}
      </Paper>
    </Box>
  )
}

const styles = {
  loader: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    height: '100%',
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
}

export default UserActivityFeedView
